import { useParams, useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import { experimentsData } from "@/mocks/experiments";
import { getExperimentDetail } from "@/mocks/experimentDetail";
import AgentConfigPanel from "./components/AgentConfigPanel";
import EditorPanel from "./components/EditorPanel";
import PreviewPanel from "./components/PreviewPanel";

export default function ExperimentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [title, setTitle] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [showPublish, setShowPublish] = useState(false);
  const [publishNote, setPublishNote] = useState("");
  const [saved, setSaved] = useState(false);
  const [activeVersion, setActiveVersion] = useState("v12.4");
  const historyItems = ["v12.4 - 当前版本", "v12.3 - 调整记忆配置", "v12.2 - 优化开场白"];
  const debugItems = ["模型：S1-Base-Pro", "状态：运行中", "最近一次发布：2026-04-27 10:24"];

  const experiment = experimentsData.find((e) => e.id === id);
  const detail = getExperimentDetail(id ?? "1");

  const displayTitle = title || experiment?.title || "实验详情";
  const titleEdited = useMemo(() => title.trim().length > 0 && title !== (experiment?.title ?? ""), [title, experiment?.title]);

  const handlePublish = () => {
    setShowPublish(true);
    setTimeout(() => setShowPublish(false), 2500);
  };

  const handleOpenHistory = () => setShowHistory((v) => !v);
  const handleOpenDebug = () => setShowDebug((v) => !v);

  const handleSaveTitle = () => {
    if (!title.trim()) return;
    setSaved(true);
    setTimeout(() => setSaved(false), 1800);
    setIsEditingTitle(false);
  };

  return (
    <div className="flex flex-col h-screen bg-[#F7F8FA] overflow-hidden text-[#1D2129]">
      {/* Top Bar */}
      <div className="flex flex-col flex-shrink-0 bg-white border-b border-[#E5E6EB] shadow-[0_1px_0_rgba(15,23,42,0.02)]">
        <div className="h-[60px] px-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <button
              onClick={() => navigate("/experiments")}
              className="flex items-center gap-1.5 text-[13px] text-[#646479] hover:text-[#1D2129] transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-s-line text-[15px]"></i>
              </div>
              实验项目
            </button>
            <span className="text-[#E5E6EB] flex-shrink-0">/</span>
            <div className="w-8 h-8 rounded-full bg-[#111827] flex items-center justify-center text-white text-sm font-semibold flex-shrink-0 overflow-hidden">
              {experiment?.coverImage ? (
                <img src={experiment.coverImage} alt="" className="w-full h-full object-cover" />
              ) : (
                <span>实</span>
              )}
            </div>
            <div className="min-w-0 flex items-center gap-2">
              <div className="min-w-0">
                {isEditingTitle ? (
                  <input
                    autoFocus
                    value={displayTitle}
                    onChange={(e) => setTitle(e.target.value)}
                    onBlur={handleSaveTitle}
                    onKeyDown={(e) => e.key === "Enter" && handleSaveTitle()}
                    className="text-[14px] font-semibold text-[#1D2129] bg-transparent outline-none border-b border-[#1D2129] w-[220px]"
                  />
                ) : (
                  <span className="block text-[14px] font-semibold text-[#1D2129] truncate max-w-[280px]">
                    {displayTitle}
                  </span>
                )}
              </div>
              <button
                onClick={() => setIsEditingTitle(true)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-[#646479] hover:text-[#1D2129] hover:bg-[#F5F6F9] transition-colors cursor-pointer flex-shrink-0"
              >
                <i className="ri-edit-line text-xs"></i>
              </button>
              {saved && <span className="text-[11px] text-[#00B42A] flex-shrink-0">已保存</span>}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={handleOpenHistory} className="h-8 px-3.5 rounded-lg border border-[#E5E6EB] bg-white text-[12px] text-[#4E5969] hover:bg-[#F5F6F9] hover:border-[#C9CDD4] transition-colors flex items-center gap-1.5 whitespace-nowrap">
              <i className="ri-history-line text-[12px]"></i>
              <span>历史版本</span>
            </button>
            <button onClick={handleOpenDebug} className="h-8 px-3.5 rounded-lg border border-[#E5E6EB] bg-white text-[12px] text-[#4E5969] hover:bg-[#F5F6F9] hover:border-[#C9CDD4] transition-colors flex items-center gap-1.5 whitespace-nowrap">
              <i className="ri-bug-line text-[12px]"></i>
              <span>调试</span>
            </button>
            <button onClick={handlePublish} className="h-8 px-4 rounded-lg bg-[#3D69E8] text-white text-[12px] font-medium hover:bg-[#2d57d1] transition-colors flex items-center gap-1.5 whitespace-nowrap shadow-sm">
              <i className="ri-send-plane-line text-[12px]"></i>
              <span>发布</span>
            </button>
          </div>
        </div>

      </div>

      {/* Three-panel Layout */}
      <div className="flex flex-1 overflow-hidden min-h-0">
        {/* Left Panel — Agent配置 (~280px) */}
        <div className="w-[280px] flex-shrink-0 border-r border-[#E5E6EB] overflow-hidden flex flex-col bg-white">
          <AgentConfigPanel content={detail.agentConfig} />
        </div>

        {/* Middle Panel — 编辑 (~320px) */}
        <div className="w-[320px] flex-shrink-0 border-r border-[#E5E6EB] overflow-hidden flex flex-col bg-white">
          <EditorPanel detail={detail} />
        </div>

        {/* Right Panel — 预览与调试 (flex-1) */}
        <div className="flex-1 overflow-hidden flex flex-col bg-[#FAFAFA]">
          <PreviewPanel content={detail.previewContent} agentType={detail.agentType} />
        </div>
      </div>
    </div>
  );
}
